import { Component, HostListener, Inject, ChangeDetectionStrategy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FileModalData, IFileViewerFileWithId } from './file-viewer.interface';
import { FileSystemService } from 'src/app/services/file-system.service';
import { AudioService } from 'src/app/services/audio.service';

@Component({
  selector: 'app-file-viewer',
  templateUrl: './file-viewer.component.html',
  styleUrls: ['./file-viewer.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FileViewerComponent {
  files: IFileViewerFileWithId[] = [];
  currentIndex = 0;
  currentFile!: IFileViewerFileWithId & { isLoading?: boolean; url?: string };

  // Zoom properties
  scale = 1;
  translateX = 0;
  translateY = 0;
  isDragging = false;
  dragStartX = 0;
  dragStartY = 0;
  minScale = 0.5;
  maxScale = 5;
  zoomStep = 0.2;

  // Touch properties
  initialDistance = 0;
  initialScale = 1;
  lastTouchX = 0;
  lastTouchY = 0;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: FileModalData,
    private dialogRef: MatDialogRef<FileViewerComponent>,
    private fileSystemService: FileSystemService,
    readonly audioService: AudioService,
  ) {
    this.files = data.files;
    this.currentIndex = data.currentIndex;
    this.setCurrentFile(this.currentIndex);
    this.loadFile();
  }

  @HostListener('window:keydown', ['$event'])
  handleKeyboardEvent(event: KeyboardEvent): void {
    if (event.key === 'ArrowLeft') this.changeCurrentFile(null, -1);
    if (event.key === 'ArrowRight') this.changeCurrentFile(null, 1);
    if (event.key === '+' || event.key === '=') {
      event.preventDefault();
      this.zoomIn();
    }
    if (event.key === '-' || event.key === '_') {
      event.preventDefault();
      this.zoomOut();
    }
    if (event.key === '0') {
      event.preventDefault();
      this.resetZoom();
    }
  }

  setCurrentFile(index: number): void {
    const file = this.files[index];
    this.currentFile = { ...file, isLoading: true, url: '' };
    this.resetZoom();
  }

  changeCurrentFile(event: MouseEvent | null = null, shift: number): void {
    if (event) event.stopPropagation();
    const index = this.currentIndex + shift;
    if (index < 0 || index >= this.files.length) return;
    this.currentIndex = index;
    this.setCurrentFile(this.currentIndex);
    this.loadFile();
  }

  loadFile(): void {
    const file = this.files[this.currentIndex];
    this.currentFile.isLoading = true;
    setTimeout(() => {
      this.currentFile.url = file.url;
      if (this.currentFile.type === 'audio') {
        this.audioService.setCurrentAudio(this.currentFile.url);
      }
    });
  }

  closeModal(): void {
    this.dialogRef.close();
  }

  fileLoaded(): void {
    this.currentFile.isLoading = false;
  }

  // Zoom methods
  onWheel(event: WheelEvent): void {
    if (this.currentFile.type !== 'image') return;
    event.preventDefault();
    event.stopPropagation();

    const delta = event.deltaY > 0 ? -this.zoomStep : this.zoomStep;
    const newScale = Math.max(this.minScale, Math.min(this.maxScale, this.scale + delta));

    if (newScale !== this.scale) {
      this.scale = newScale;
      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      }
    }
  }

  zoomIn(): void {
    if (this.currentFile.type !== 'image') return;
    this.scale = Math.min(this.maxScale, this.scale + this.zoomStep);
  }

  zoomOut(): void {
    if (this.currentFile.type !== 'image') return;
    this.scale = Math.max(this.minScale, this.scale - this.zoomStep);
    if (this.scale === 1) {
      this.translateX = 0;
      this.translateY = 0;
    }
  }

  resetZoom(): void {
    this.scale = 1;
    this.translateX = 0;
    this.translateY = 0;
  }

  onImageDoubleClick(event: MouseEvent): void {
    event.stopPropagation();
    this.resetZoom();
  }

  onMouseDown(event: MouseEvent): void {
    if (this.currentFile.type !== 'image' || this.scale === 1) return;
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
    this.dragStartX = event.clientX - this.translateX;
    this.dragStartY = event.clientY - this.translateY;
  }

  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: MouseEvent): void {
    if (!this.isDragging) return;
    event.preventDefault();
    this.translateX = event.clientX - this.dragStartX;
    this.translateY = event.clientY - this.dragStartY;
  }

  @HostListener('window:mouseup')
  onMouseUp(): void {
    this.isDragging = false;
  }

  getImageTransform(): string {
    return `translate(${this.translateX}px, ${this.translateY}px) scale(${this.scale})`;
  }

  getImageCursor(): string {
    if (this.currentFile.type !== 'image') return 'default';
    if (this.scale > 1) return this.isDragging ? 'grabbing' : 'grab';
    return 'default';
  }

  // Touch methods for mobile
  onTouchStart(event: TouchEvent): void {
    if (this.currentFile.type !== 'image') return;

    if (event.touches.length === 2) {
      // Pinch to zoom
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      this.initialDistance = this.getDistance(touch1, touch2);
      this.initialScale = this.scale;
    } else if (event.touches.length === 1 && this.scale > 1) {
      // Pan with one finger when zoomed
      event.preventDefault();
      const touch = event.touches[0];
      this.isDragging = true;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
    }
  }

  onTouchMove(event: TouchEvent): void {
    if (this.currentFile.type !== 'image') return;

    if (event.touches.length === 2) {
      // Pinch to zoom
      event.preventDefault();
      const touch1 = event.touches[0];
      const touch2 = event.touches[1];
      const currentDistance = this.getDistance(touch1, touch2);
      const scaleChange = currentDistance / this.initialDistance;
      this.scale = Math.max(this.minScale, Math.min(this.maxScale, this.initialScale * scaleChange));

      if (this.scale === 1) {
        this.translateX = 0;
        this.translateY = 0;
      }
    } else if (event.touches.length === 1 && this.isDragging && this.scale > 1) {
      // Pan with one finger
      event.preventDefault();
      const touch = event.touches[0];
      const deltaX = touch.clientX - this.lastTouchX;
      const deltaY = touch.clientY - this.lastTouchY;
      this.translateX += deltaX;
      this.translateY += deltaY;
      this.lastTouchX = touch.clientX;
      this.lastTouchY = touch.clientY;
    }
  }

  onTouchEnd(event: TouchEvent): void {
    if (this.currentFile.type !== 'image') return;

    if (event.touches.length === 0) {
      this.isDragging = false;
      this.initialDistance = 0;
    }
  }

  private getDistance(touch1: Touch, touch2: Touch): number {
    const dx = touch1.clientX - touch2.clientX;
    const dy = touch1.clientY - touch2.clientY;
    return Math.sqrt(dx * dx + dy * dy);
  }
}
